import type { Query, Mutation, Subscription, Variables, Result } from '@/api';
import type { GraphQLResult } from '@aws-amplify/api-graphql';
import type Observable from 'zen-observable-ts';
import type { ZenObservable } from 'zen-observable-ts';
import * as tc from '@/utils/typeChecks';
import { API, graphqlOperation } from 'aws-amplify';
import * as mutations from '@/graphql/mutations';
import * as queries from '@/graphql/queries';
import * as subscriptions from '@/graphql/subscriptions';
import * as Sentry from '@sentry/react';
import { logger } from './logger';
import Organization from '@/services/organization';
import Workspace from '@/services/workspace';
import { getReduxState } from './reduxSaga';
import { addBackendSuffix } from './namingConvention';

type Context = { orgId: string; wsSeq: string };

const context = { currentOrganization: '', currentWorkspace: '', orgId: '', wsSeq: '' };

// AppSync returns HTTP 200 with BOTH a partial `data` payload AND a top-level
// `errors` array when a nested field fails an @auth check (e.g. an orphaned
// WorkspaceUser referencing a workspace whose organization the current user is
// not authorized to read - the offending nested field is nulled). Amplify v4
// surfaces this by *rejecting* the promise with the GraphQLResult object, even
// though usable data is present. Detect that shape so we can recover the
// partial data instead of letting the whole query - and the app - fail.
//
// Per the GraphQL spec, a response has at most three top-level keys - `data`,
// `errors`, `extensions` - and at least one of `data`/`errors` is always
// present. That gives exactly four cases, and this function's job is to pick
// out only the one that carries recoverable data:
//   1. `data` present, no `errors`      -> clean success. Amplify resolves the
//                                           promise, so we never reach here.
//   2. `data` null/absent + `errors`    -> request error (syntax/validation);
//                                           resolvers never ran. Not a partial
//                                           success -> fails the `data == null`
//                                           guard below and gets thrown.
//   3. `data` with >=1 non-null field   -> genuine field-level partial success.
//        + `errors`                        Returns true -> caller recovers it.
//   4. `data` wrapper present but ALL    -> field error where the queried
//        requested fields null + `errors`   field(s) failed. AppSync still
//                                           returns the wrapping object (e.g.
//                                           `{ beemAgentCheckRedshiftQuery: null }`
//                                           with a `Lambda:Unhandled` error), so
//                                           a truthy top-level `data` is NOT
//                                           enough - there is nothing to
//                                           recover. Returns false -> thrown.
//
// Verified against the official docs (2026-09):
//   - GraphQL response format: https://graphql.org/learn/response/
//   - GraphQL spec, Section 7 (Response): https://spec.graphql.org/draft/#sec-Response
//   - AppSync errors/errorType: https://docs.aws.amazon.com/appsync/latest/devguide/troubleshooting-and-common-mistakes.html
//
// Case 4 was a production bug: the original check only tested `data != null` at
// the top level, so it misclassified the null-field-plus-error response as a
// partial success and swallowed a real failure. The `.some(...)` check below is
// what distinguishes case 3 (recover) from case 4 (throw).
function isGraphQLPartialSuccess(
  e: unknown,
): e is GraphQLResult<Record<string, unknown>> & { errors: NonNullable<GraphQLResult['errors']> } {
  if (
    typeof e !== 'object' ||
    e === null ||
    !('data' in e) ||
    (e as GraphQLResult).data == null ||
    !('errors' in e) ||
    !Array.isArray((e as GraphQLResult).errors) ||
    ((e as GraphQLResult).errors?.length ?? 0) === 0
  ) {
    // Not the right overall shape, or no `data`/no `errors` (cases 1 and 2).
    return false;
  }
  // Shape looks like a partial success, but distinguish case 3 from case 4: a
  // non-null top-level `data` wrapper is not enough, since AppSync returns it
  // even when every requested field is nulled. Only recover when at least one
  // field inside `data` actually resolved to a non-null value.
  //
  // `__typename` is excluded defensively: today it is only ever selected
  // *nested inside* a resolved field's value (see queries.ts), never as a
  // sibling of the top-level operation field, so it would not reach this
  // object. But should a future query shape ever place a metadata field at
  // this level, counting it as "usable data" would resurrect the exact
  // case-4 misclassification this function exists to prevent.
  const data = (e as GraphQLResult).data as Record<string, unknown>;
  return Object.entries(data).some(([key, value]) => key !== '__typename' && value != null);
}

export async function getContext(
  currentOrganization: string,
  currentWorkspace: string,
): Promise<Context> {
  if (currentOrganization !== context.currentOrganization) {
    const org = await Organization.getOrganizationById(currentOrganization);
    context.currentOrganization = currentOrganization;
    context.orgId = org.cloudResourceId;
  }
  if (currentWorkspace !== context.currentWorkspace) {
    const ws = await Workspace.getWorkspaceById(currentWorkspace);
    context.currentWorkspace = currentWorkspace;
    context.wsSeq = addBackendSuffix(String(ws.seq));
  }
  return { orgId: context.orgId, wsSeq: context.wsSeq };
}

/**
 * Issue the request and hand back a result, whether it came from a clean
 * response or from a recoverable partial success.
 *
 * Split out of `graphql` so that every path either returns a value or throws:
 * the caller gets a plain `const`, with no definite-assignment assertion
 * papering over a variable that a future branch could leave unset.
 */
async function execute<T extends Query | Mutation>(
  command: T,
  query: unknown,
  variables: Variables<T>,
): Promise<GraphQLResult<Record<string, Result<T>>>> {
  try {
    if (command.startsWith('beemAgent') || command === 'fivetranCreateConnectCard') {
      // @ts-expect-error this is ok because this method is carefully verified already
      const temp = variables.query ?? variables.input;
      if (temp && !temp.orgId) {
        const { currentOrganization, currentWorkspace } = getReduxState().organization;
        tc.assertIsString(currentOrganization, 'BEEM240128174601');
        tc.assertIsString(currentWorkspace, 'BEEM240128174602');
        const ctx = await getContext(currentOrganization, currentWorkspace);
        temp.orgId = ctx.orgId;
        if (command !== 'fivetranCreateConnectCard') temp.wsSeq = ctx.wsSeq;
      }
    }
    return (await API.graphql(graphqlOperation(query, variables))) as GraphQLResult<
      Record<string, Result<T>>
    >;
  } catch (e) {
    let debugContext = `[graphql] command=${command}`;
    try {
      debugContext += ` variables=${JSON.stringify(variables)}`;
    } catch {
      debugContext += ' variables=[unserializable]';
    }
    // Partial success: keep the usable data and carry on. Callers must be
    // resilient to null/missing items (see e.g. Workspace.getWorkspacesByUser).
    if (isGraphQLPartialSuccess(e)) {
      logger.warn({
        label: 'graphql partial success',
        message: `${debugContext} errors=${JSON.stringify(e.errors)}`,
      });
      // Every partial success is a silently degraded response, so report it
      // here rather than relying on each caller to notice. Command and error
      // messages only - `variables` can carry customer data and has no
      // business being shipped to Sentry, which is why debugContext stays in
      // the local log.
      Sentry.captureMessage(`graphql partial success: ${command}`, {
        level: 'warning',
        extra: {
          command,
          errors: e.errors.map((err) => err.message),
        },
      });
      return e as GraphQLResult<Record<string, Result<T>>>;
    }
    logger.debug({ label: 'graphql error', message: debugContext });
    if (typeof e === 'object' && e !== null) {
      (e as Record<string, unknown>).__debugContext = debugContext;
    }
    throw e;
  }
}

export async function graphql<T extends Query | Mutation>(
  command: T,
  variables: Variables<T>,
  rnt = false, // temporary "return-next-token" to not follow the nextToken below
): Promise<Result<T>> {
  // @ts-expect-error this is ok because the logic is confirmed to be good, perhaps there is a better way but it's not worth it
  const query = queries[command] ?? mutations[command];
  const rs = await execute<T>(command, query, variables);

  tc.assertIsDefined(rs.data, 'BEEM211105120248');
  const pos = command.indexOf('Only');
  // return rs.data[pos > 0 ? command.substring(0, pos) : command];

  // this code is kind of temporary because we will want to implement pagination
  // instead of just plainly following the nextToken and grab all the records,
  // which potentially can result in a lot of requests for 1000+ records
  // that's why I didn't spend time to polish the tsc errors
  const rs1 = rs.data[pos > 0 ? command.substring(0, pos) : command];
  // @ts-expect-error this is ok because this part is temporary and this method is carefully verified already
  if (!rnt && rs1?.nextToken) {
    // @ts-expect-error this is ok because this part is temporary and this method is carefully verified already
    if (variables.limit && rs1.items.length >= variables.limit) return rs1;
    // @ts-expect-error this is ok because this part is temporary and this method is carefully verified already
    const { items, nextToken } = rs1;
    // @ts-expect-error this is ok because this part is temporary and this method is carefully verified already
    const { items: moreItems, nextToken: moreNextToken } = await graphql(command, {
      ...variables,
      nextToken,
    });
    // @ts-expect-error this is ok because this part is temporary and this method is carefully verified already
    return { items: items.concat(moreItems), nextToken: moreNextToken };
  }
  return rs1;
}

export function subscribe<T extends Subscription>(
  name: T,
  variables: Variables<T>,
  handler: (result: Result<T>, done: () => void) => void,
): ZenObservable.Subscription {
  const rs = API.graphql(graphqlOperation(subscriptions[name], variables)) as Observable<
    Record<string, unknown>
  >;
  const subscription = rs.subscribe({
    next: (event: { value: { data: T } }) => {
      tc.assertIsDefined(event.value.data, 'BEEM211105120249');
      // @ts-expect-error this is ok because the logic is confirmed to be good, perhaps there is a better way but it's not worth it
      handler(event.value.data[name], () => {
        subscription.unsubscribe();
      });
    },
  });
  return subscription;
}

const findMatchingClosingBracket = (text: string, openBracketIndex: number): number => {
  const nextClosingBracketIndex = text.indexOf('}', openBracketIndex);
  const nextOpenBracketIndex = text.indexOf('{', openBracketIndex + 1);
  if (nextOpenBracketIndex > openBracketIndex && nextOpenBracketIndex < nextClosingBracketIndex)
    return text.indexOf('}', findMatchingClosingBracket(text, nextOpenBracketIndex) + 1);
  return nextClosingBracketIndex;
};

export const removeSubObjectFromQuery = (query: string, subObjectName: string): string => {
  const start = query.indexOf(`${subObjectName} {`);
  const openBracPos = query.indexOf('{', start);
  const closeBracPos = findMatchingClosingBracket(query, openBracPos);
  return query.substring(0, start) + query.substring(closeBracPos + 1);
};
